import React from "react";

import moment from "moment";
import css from "classnames";
import { QueryResult, useQuery } from "@apollo/client";

import Copyable from "components/copyable";
import { FeatureFlag } from "components/feature";
import PhoneNumber from "components/phone-number/phone-number";
import { ServerError } from "components/graphql-views";
import interestSubmission from "pages/eligibility/query/form-submission-id.gql";
import { Feature, QueryInterestProfile, QueryInterestProfileVariables,
  QueryInterestProfile_interest as InterestType,
  EligibilitySubmissionId, EligibilitySubmissionIdVariables } from "types/gql-types";
import styles from "./details.module.css";
import ContactInformation from "./profile-details/contact-information";
import EligibilityInformation from "./profile-details/eligibility-information";
import Overview from "./profile-details/overview";
import Status from "./profile-details/status";
import Heading from "./profile-details/heading";
import Row from "./profile-details/row";


type Props = {
  query: QueryResult<QueryInterestProfile, QueryInterestProfileVariables>;
}

const AltContactInformation = ({ interest }: { interest: InterestType }) => (
  <div className={styles.group}>
    <Heading text="Alternate Contact Info" />
    <Row title="Name">{interest.contactName}</Row>
    <Row title="Relationship">{interest.contactRelation}</Row>
    <Row title="Email">{interest.contactEmail}</Row>
    <Row title="Phone">
      {interest.contactPrimaryPhone && (
        <PhoneNumber number={interest.contactPrimaryPhone} ext={interest.contactPrimaryPhoneExt} />
      )}
    </Row>
  </div>);

const DetailsSidebar: React.FC<Props> = ({ query }) => {

  if (query.loading) return null;
  if (!query.data?.interest?.interestList?.property) {
    return <ServerError desc="There was a problem loading the page." />;
  }
  const { data: { interest } } = query;

  return (
    <div className={css(styles.container, "card")}>
      <Status interest={interest} />
      <FeatureFlag>
        {(enabled) => {
          if (!enabled(Feature.ELIGIBILITY_FORM)) return null;
          const context =
          useQuery<EligibilitySubmissionId, EligibilitySubmissionIdVariables>(interestSubmission, {
            variables: { interest: interest.id },
          });
          const submissionID = context.data?.interest?.eligibilitySubmission.id;
          if (!submissionID) return null;
          return (
            <div className={styles.group}>
              <Heading text="Eligibility" />
              <Copyable text={`${window.location.origin}/application/${submissionID}`} />
              <br />
              <em>Provide this link to an applicant to fill out the eligibility form.</em>
            </div>
          );
        }}
      </FeatureFlag>

      <ContactInformation interest={interest} />

      <EligibilityInformation interest={interest} />

      {interest.prefMoveInDate && (
        <div className={styles.group}>
          <Heading text="Preferences" />
          <Row title="Preferred Move-In Date">
            {moment(`${interest.prefMoveInDate}Z`).local().format("M/D/YY")}
          </Row>
        </div>
      )}

      <AltContactInformation interest={interest} />

      <Overview interest={interest} />
    </div>
  );
};

export default DetailsSidebar;
